import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone } from 'lucide-react';
import { useGetAllEnquiryQuery } from '../store/enquiryApi';
import { useSelector } from 'react-redux';
import { Enquiry, RootState } from '@/types';
import { Dialog, DialogContent } from '@/components/ui/dialog';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import EnquiryActionsDropdown from './EnquiryOptionDropdown';
import AddEnquiry from './AddEnquiry';

const EnquiryManagement = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const { data: enquiries = [] } = useGetAllEnquiryQuery(user?.token);

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [filterStatus, setFilterStatus] = React.useState('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [filterMonth, setFilterMonth] = React.useState('');
  const [filterYear, setFilterYear] = React.useState('');
  const itemsPerPage = 10;

  // Function to filter enquiries based on search, status, month, and year
  const filteredEnquiries = enquiries?.filter((enquiry: Enquiry) => {
    const matchesStatus =
      filterStatus === 'all' || enquiry.status === filterStatus;

    const matchesSearch =
      enquiry?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      enquiry?.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      enquiry?.Enquirymode.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesMonth =
      !filterMonth ||
      new Date(enquiry.date)
        .toLocaleString('default', { month: 'short' })
        .toLowerCase() === filterMonth;

    const matchesYear =
      !filterYear ||
      new Date(enquiry.date).getFullYear().toString() === filterYear;

    return matchesStatus && matchesSearch && matchesMonth && matchesYear;
  }).reverse();

  // Pagination calculations
  const totalPages = Math.ceil(filteredEnquiries.length / itemsPerPage);
  const paginatedEnquiries = filteredEnquiries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  React.useEffect(() => {
    setCurrentPage(1); // Reset page to 1 whenever filters change
  }, [filterStatus, searchQuery, filterMonth, filterYear]);

  return (
    <div className="container mx-auto px-4 py-6 max-w-fit">
      <Card className="max-w-[75vw]">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0 pb-6">
          <CardTitle className="text-2xl font-bold">
            <h1>View Enquiry</h1>
          </CardTitle>
          {user?.role == 'admin' || (
            <Button
              variant="default"
              className="bg-[#26BDB0] hover:bg-[#49ccc1e2] text-white w-full sm:w-auto"
              onClick={() => setIsDialogOpen(true)}
            >
              + Add New Enquiry
            </Button>
          )}
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="lg:col-span-2">
              <Input
                placeholder="Enter Student Name, address, enquiry mode"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className=""
              />
            </div>
            <div>
              <Select
                value={filterMonth}
                onValueChange={(value) => setFilterMonth(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jan">January</SelectItem>
                  <SelectItem value="feb">February</SelectItem>
                  <SelectItem value="mar">March</SelectItem>
                  <SelectItem value="apr">April</SelectItem>
                  <SelectItem value="may">May</SelectItem>
                  <SelectItem value="jun">June</SelectItem>
                  <SelectItem value="jul">July</SelectItem>
                  <SelectItem value="aug">August</SelectItem>
                  <SelectItem value="sep">September</SelectItem>
                  <SelectItem value="oct">October</SelectItem>
                  <SelectItem value="nov">November</SelectItem>
                  <SelectItem value="dec">December</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select
                value={filterYear}
                onValueChange={(value) => setFilterYear(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 lg:col-span-1">
              <Button className="bg-[#26BDB0] hover:bg-[#49ccc1e2] flex-1 sm:flex-none">
                Search
              </Button>
              <Button
                variant="outline"
                className="flex-1 sm:flex-none"
                onClick={() => {
                  setSearchQuery('');
                  setFilterStatus('all');
                  setFilterMonth('');
                  setFilterYear('');
                }}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap text-gray-700 gap-2 mb-6">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'ghost'}
              onClick={() => setFilterStatus('all')}
              className="text-sm"
            >
              All
              <Badge className="ml-2 bg-blue-600">{enquiries?.length}</Badge>
            </Button>
            <Button
              variant={filterStatus === 'Open' ? 'default' : 'ghost'}
              onClick={() => setFilterStatus('Open')}
              className="text-sm"
            >
              Open
              <Badge className="ml-2 bg-[#26BDB0]">
                {enquiries.filter((e: Enquiry) => e.status === 'Open').length}
              </Badge>
            </Button>
            <Button
              variant={filterStatus === 'Registered' ? 'default' : 'ghost'}
              onClick={() => setFilterStatus('Registered')}
              className="text-sm"
            >
              Registered
              <Badge className="ml-2 bg-[#26BDB0]">
                {enquiries.filter((e: Enquiry) => e.status === 'Registered').length}
              </Badge>
            </Button>
            <Button
              variant={filterStatus === 'Closed' ? 'default' : 'ghost'}
              onClick={() => setFilterStatus('Closed')}
              className="text-sm"
            >
              Close
              <Badge className="ml-2 bg-red-600">
                {enquiries.filter((e: Enquiry) => e.status === 'Closed').length}
              </Badge>
            </Button>
            <Button
              variant={filterStatus === 'Pending' ? 'default' : 'ghost'}
              onClick={() => setFilterStatus('Pending')}
              className="text-sm"
            >
              Pending
              <Badge className="ml-2 bg-yellow-600">
                {
                  enquiries.filter((e: Enquiry) => e.status === 'Pending')
                    .length
                }
              </Badge>
            </Button>
          </div>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-7xl min-h-screen text-foreground bottom-0 overflow-y-auto">
              <AddEnquiry enquiry={''} setIsDialogOpen={setIsDialogOpen} />
            </DialogContent>
          </Dialog>

          <div className="border rounded-lg  overflow-auto max-w-6xl">
            <div className="overflow-x-auto">
              <table className=" text-sm text-left">
                <thead className="text-xs bg-muted whitespace-nowrap">
                  <tr>
                    <th className="px-4 py-3 font-medium">No.</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Qualification</th>
                    <th className="px-4 py-3 font-medium">Address</th>
                    <th className="px-4 py-3 font-medium">Sent Reminder</th>
                    <th className="px-4 py-3 font-medium">Interested Course</th>
                    <th className="px-4 py-3 font-medium">Enquiry Mode</th>
                    <th className="px-4 py-3 font-medium">Learning Mode</th>
                    <th className="px-4 py-3 font-medium">Reference</th>
                    <th className="px-4 py-3 font-medium">Remark</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    {user?.role === 'hr' && (
                      <th className="px-4 py-3 font-medium">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y ">
                  {paginatedEnquiries.map((enquiry: Enquiry, index: number) => (
                    <tr
                      key={enquiry._id}
                      className="bg-background hover:bg-muted whitespace-nowrap"
                    >
                      <td className="px-4 py-3">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-3">
                        {new Date(enquiry.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">{enquiry.name}</td>
                      <td className="px-4 py-3">{enquiry.qualification}</td>
                      <td className="px-4 py-3">{enquiry.address}</td>
                      <td className="px-4 py-3">
                        <button className="inline-flex items-center text-[#26BDB0] hover:text-[#49ccc1e2]">
                          <Phone className="w-4 h-4 mr-2" />
                          {enquiry.contact}
                        </button>
                      </td>
                      <td className="px-4 py-3">{enquiry.course}</td>
                      <td className="px-4 py-3">{enquiry.Enquirymode}</td>
                      <td className="px-4 py-3">{enquiry.mode}</td>
                      <td className="px-4 py-3">{enquiry.ref}</td>
                      <td className="px-4 py-3">{enquiry.remark}</td>
                      <td className="px-4 py-3">{enquiry.status}</td>
                      {user?.role === 'hr' ? (
                        <>
                          <td className="px-4 py-3">
                            <EnquiryActionsDropdown enquiry={enquiry} />
                          </td>
                        </>
                      ) : (
                        <></>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
            <div className="text-sm text-gray-500 order-2 sm:order-1">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredEnquiries.length)}{' '}
              of {filteredEnquiries.length} entries
            </div>
            <div className="flex flex-wrap justify-center gap-2 order-1 sm:order-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {/* {Array.from({ length: totalPages }).map((_, index) => (
                <Button
                  key={index}
                  variant={currentPage === index + 1 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Button>
              ))} */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EnquiryManagement;
